import { Component, DestroyRef, inject, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { combineLatest } from 'rxjs';
import { ProductGridSectionComponent, SortOption } from '../components/product-grid-section.component';
import { BestSellerProduct, CatalogSidebarComponent, CategoryItem, PriceFilterEvent } from '../components/catalog-sidebar.component';
import { ProductStore } from '../../../core/models/interfaces-general';
import { DRAWER_WIDTHS, DrawerService } from '../../../services/drawer-service';
import { CategoriaService } from '../../../core/services/categoria.service';
import { ProductoService } from '../../../core/services/producto.service';
import { CategoriaMapper } from '../../../core/mappers/categoria.mapper';
import { ProductoMapper } from '../../../core/mappers/producto.mapper';
import { Constante } from '../../../constante';
import { UiLoadingComponent } from '../../../shared/components/ui-loading.component';

@Component({
    selector: 'app-catalogo-base',
    imports: [
        CommonModule,
        ProductGridSectionComponent,
        CatalogSidebarComponent,
        RouterLink,
        UiLoadingComponent
    ],
    templateUrl: './catalogo-base-component.html',
    styles: ''
})
export class CatalogoBaseComponent implements OnInit {
    private drawerService = inject(DrawerService);
    private categoriaService = inject(CategoriaService);
    private productoService = inject(ProductoService);
    private route = inject(ActivatedRoute);
    private destroyRef = inject(DestroyRef);

    misCategorias = signal<CategoryItem[]>([]);
    misMasVendidos = signal<BestSellerProduct[]>([]);
    productos = signal<ProductStore[]>([]);
    total = signal(0);
    titulo = signal('Catálogo');
    breadcrumb = signal('CATÁLOGO');
    pageSize = signal(Constante.PAGINATION_SIZE);
    currentSort = signal<SortOption>('default');
    minPrice = signal(0);
    maxPrice = signal(500);
    loading = signal(true);

    private categoriaUuid: string | null = null;
    private searchKey = '';

    drawerFiltro = viewChild.required<TemplateRef<unknown>>('drawerFiltro');

    ngOnInit(): void {
        this.productoService.listMasVendidos().subscribe({
            next: (items) => {
                this.misMasVendidos.set(items.map((item) => ({
                    id: item.uuid,
                    title: item.nombre,
                    price: item.precioFinal,
                    imageUrl: item.urlImagen || '',
                    slug: item.slug
                })));
            }
        });

        combineLatest([this.route.paramMap, this.route.queryParamMap])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([params, query]) => {
                this.searchKey = query.get('key') ?? '';
                const slug = params.get('slug');
                this.categoriaService.listCategorias().subscribe({
                    next: (categorias) => {
                        this.misCategorias.set(categorias.map((item) => CategoriaMapper.toSidebar(item)));
                        if (slug) {
                            const found = CategoriaMapper.findBySlug(categorias, slug);
                            this.categoriaUuid = found?.uuid ?? null;
                            this.titulo.set(found?.nombre ?? slug);
                            this.breadcrumb.set((found?.nombre ?? slug).toUpperCase());
                        } else {
                            this.categoriaUuid = null;
                            this.titulo.set(this.searchKey ? `Resultados: ${this.searchKey}` : 'Catálogo');
                            this.breadcrumb.set('CATÁLOGO');
                        }
                        this.loadProductos();
                    }
                });
            });
    }

    onFiltrarPrecios(event: PriceFilterEvent) {
        this.minPrice.set(event.minPrice);
        this.maxPrice.set(event.maxPrice);
        this.loadProductos();
    }

    onSortChange(sort: SortOption) {
        this.currentSort.set(sort);
        this.loadProductos();
    }

    onPageSizeChange(size: number) {
        this.pageSize.set(size);
        this.loadProductos();
    }

    onOpenDrawerFiltro() {
        this.drawerService.open({
            title: 'Filtros',
            content: this.drawerFiltro(),
            width: DRAWER_WIDTHS.XXLARGEMOVIL,
            resizable: false,
        });
    }

    private loadProductos() {
        this.loading.set(true);
        this.productoService.listProductos({
            categoriaUuid: this.categoriaUuid,
            precioDesde: this.minPrice(),
            precioHasta: this.maxPrice(),
            orden: this.mapSort(this.currentSort()),
            key: this.searchKey || null,
            pageNumber: 1,
            pageSize: this.pageSize()
        }).subscribe({
            next: (page) => {
                this.productos.set((page.data ?? []).map((item) => ProductoMapper.toStore(item)));
                this.total.set(page.total ?? 0);
                this.loading.set(false);
            },
            error: () => this.loading.set(false)
        });
    }

    private mapSort(sort: SortOption): string | null {
        switch (sort) {
            case 'popular':
                return 'mas_populares';
            case 'rating':
                return 'mejor_valorados';
            case 'recent':
                return 'mas_recientes';
            case 'price-asc':
                return 'precio_asc';
            case 'price-desc':
                return 'precio_desc';
            default:
                return null;
        }
    }
}
